import React, { useState, useEffect, useCallback } from 'react';
import { tableStyles } from '../ui';
import {
  // Sales Follow-Up
  getSalesFollowUpSettings,
  updateSalesFollowUpSettings,
  seedSalesFollowUpSettings,
  getSalesFollowUpRuns,
  getSalesFollowUpRun,
  triggerSalesFollowUp,
  pauseSalesFollowUp,
  resumeSalesFollowUp,
  // Network Report
  getNetworkReportSettings,
  updateNetworkReportSettings,
  seedNetworkReportSettings,
  getNetworkReportRuns,
  triggerNetworkReport,
  pauseNetworkReport,
  resumeNetworkReport,
  // Meeting Digest
  getMeetingDigestSettings,
  updateMeetingDigestSettings,
  seedMeetingDigestSettings,
  getMeetingDigestRuns,
  triggerMeetingDigest,
  pauseMeetingDigest,
  resumeMeetingDigest,
  // Executive Calendar
  listExecutiveCalendarTasks,
  seedExecutiveCalendarTasks,
  createExecutiveCalendarTask,
  updateExecutiveCalendarTask,
  deleteExecutiveCalendarTask,
  triggerExecutiveCalendarTask,
  pauseExecutiveCalendarTask,
  resumeExecutiveCalendarTask,
  getExecutiveCalendarRuns,
  ExecutiveCalendarTask,
  // Direct Sold
  getAdminSettings,
  updateAdminSettings,
  getProcessingRuns,
  sendDirectSoldReminderTest,
  pauseDirectSold,
  resumeDirectSold,
  // RFP Inbox Scanner
  getRfpSettings,
  updateRfpSettings,
  seedRfpSettings,
  getRfpRuns,
  triggerRfpProcessing,
  pauseRfpProcessing,
  resumeRfpProcessing,
  // IO Approval Forwarder
  getIoApprovalSettings,
  updateIoApprovalSettings,
  seedIoApprovalSettings,
  getIoApprovalRuns,
  triggerIoApprovalProcessing,
  pauseIoApprovalProcessing,
  resumeIoApprovalProcessing,
} from '../../services/adminApi';

// ============================================================
// Types
// ============================================================

interface TaskCardState {
  settings: any;
  runs: any[];
  loading: boolean;
  error: string | null;
  saving: boolean;
  triggering: boolean;
  message: string | null;
  expanded: boolean;
  expandedRunId: string | null;
}

const defaultCardState: TaskCardState = {
  settings: null,
  runs: [],
  loading: true,
  error: null,
  saving: false,
  triggering: false,
  message: null,
  expanded: false,
  expandedRunId: null,
};

// ============================================================
// Helpers
// ============================================================

const formatDuration = (ms?: number) => {
  if (!ms) return '-';
  if (ms < 1000) return `${ms}ms`;
  if (ms < 60000) return `${(ms / 1000).toFixed(1)}s`;
  return `${(ms / 60000).toFixed(1)}m`;
};

const formatDate = (dateStr?: string) => {
  if (!dateStr) return '-';
  return new Date(dateStr).toLocaleString();
};

// Humanize a simple `m h * * D[,D...]` cron expression into "Mon 7:00 AM"
// style text. Falls back to the raw cron for anything more exotic.
const TZ_ABBR: Record<string, string> = {
  'America/Los_Angeles': 'PT',
  'America/Denver': 'MT',
  'America/Chicago': 'CT',
  'America/New_York': 'ET',
  'UTC': 'UTC',
};
const DAY_NAMES = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const formatCronSchedule = (cron: string, timezone: string): string => {
  const parts = cron.trim().split(/\s+/);
  if (parts.length !== 5) return `${cron} (${timezone})`;
  const [minStr, hourStr, dom, month, dow] = parts;
  const minute = Number(minStr);
  const hour = Number(hourStr);
  if (!Number.isInteger(minute) || !Number.isInteger(hour) || minute < 0 || minute > 59 || hour < 0 || hour > 23) {
    return `${cron} (${timezone})`;
  }
  const period = hour >= 12 ? 'PM' : 'AM';
  const hour12 = ((hour + 11) % 12) + 1;
  const timeStr = minute === 0 ? `${hour12} ${period}` : `${hour12}:${minute.toString().padStart(2, '0')} ${period}`;
  const tzLabel = TZ_ABBR[timezone] || timezone;

  // Daily: every day of week & day of month
  if (dom === '*' && month === '*' && dow === '*') {
    return `Daily ${timeStr} ${tzLabel}`;
  }
  // Weekly on specific day(s): m h * * D[,D...]
  if (dom === '*' && month === '*' && /^\d(,\d)*$/.test(dow)) {
    const days = dow.split(',').map(Number).filter(d => d >= 0 && d <= 6).map(d => DAY_NAMES[d]);
    if (days.length > 0) return `${days.join(', ')} ${timeStr} ${tzLabel}`;
  }
  return `${cron} (${timezone})`;
};

// ============================================================
// Component
// ============================================================

const ScheduledEmailsTab: React.FC = () => {
  // --- State for each task card ---
  const [salesState, setSalesState] = useState<TaskCardState>({ ...defaultCardState });
  const [networkState, setNetworkState] = useState<TaskCardState>({ ...defaultCardState });
  const [directSoldState, setDirectSoldState] = useState<TaskCardState>({ ...defaultCardState });
  const [rfpState, setRfpState] = useState<TaskCardState>({ ...defaultCardState });
  const [ioApprovalState, setIoApprovalState] = useState<TaskCardState>({ ...defaultCardState });
  const [meetingDigestState, setMeetingDigestState] = useState<TaskCardState>({ ...defaultCardState });

  // --- Input state for adding items ---
  const [salesNewSource, setSalesNewSource] = useState('');
  const [salesNewRecipient, setSalesNewRecipient] = useState('');
  const [salesNewExclude, setSalesNewExclude] = useState('');
  const [salesNewInternalDomain, setSalesNewInternalDomain] = useState('');
  const [salesDryRun, setSalesDryRun] = useState(false);
  const [networkNewRecipient, setNetworkNewRecipient] = useState('');
  const [directSoldNewRecipient, setDirectSoldNewRecipient] = useState('');
  const [rfpNewRecipient, setRfpNewRecipient] = useState('');
  const [rfpNewExclude, setRfpNewExclude] = useState('');
  const [ioApprovalNewRecipient, setIoApprovalNewRecipient] = useState('');
  const [ioApprovalNewSubjectPattern, setIoApprovalNewSubjectPattern] = useState('');
  const [ioApprovalNewExclude, setIoApprovalNewExclude] = useState('');
  const [meetingNewRecipient, setMeetingNewRecipient] = useState('');
  const [meetingNewParticipant, setMeetingNewParticipant] = useState('');
  const [meetingNewExecutive, setMeetingNewExecutive] = useState('');
  const [meetingDryRun, setMeetingDryRun] = useState(false);

  // ===== Executive Calendar (multi-task) =====
  const [execTasks, setExecTasks] = useState<ExecutiveCalendarTask[]>([]);
  const [execLoading, setExecLoading] = useState(true);
  const [execError, setExecError] = useState<string | null>(null);
  // Per-task: in-flight runs, expanded state, transient messages, edit buffer
  const [execCardState, setExecCardState] = useState<Record<string, {
    expanded: boolean;
    saving: boolean;
    triggering: boolean;
    message: string | null;
    error: string | null;
    runs: any[];
    expandedRunId: string | null;
    edit: ExecutiveCalendarTask | null; // working copy for the form
    dryRun: boolean;
  }>>({});
  // Per-task chip-input fields
  const [execNewExecInput, setExecNewExecInput] = useState<Record<string, string>>({});
  const [execNewRecipInput, setExecNewRecipInput] = useState<Record<string, string>>({});
  // New-task draft
  const [execShowNewForm, setExecShowNewForm] = useState(false);
  const [execNewDraft, setExecNewDraft] = useState<{
    name: string;
    executiveEmails: string[];
    recipientEmails: string[];
    scheduleCron: string;
    scheduleTimezone: string;
    daysAhead: number;
  }>({
    name: '',
    executiveEmails: [],
    recipientEmails: [],
    scheduleCron: '0 6 * * 0',
    scheduleTimezone: 'America/Los_Angeles',
    daysAhead: 7,
  });
  const [execNewDraftExecInput, setExecNewDraftExecInput] = useState('');
  const [execNewDraftRecipInput, setExecNewDraftRecipInput] = useState('');
  const [execCreating, setExecCreating] = useState(false);

  // ============================================================
  // Sales Follow-Up Data Loading
  // ============================================================
  const loadSalesData = useCallback(async () => {
    setSalesState(prev => ({ ...prev, loading: true, error: null }));
    try {
      const [settingsRes, runsRes] = await Promise.all([
        getSalesFollowUpSettings(),
        getSalesFollowUpRuns(10),
      ]);

      let settings = settingsRes?.settings;
      if (!settings) {
        await seedSalesFollowUpSettings();
        const seeded = await getSalesFollowUpSettings();
        settings = seeded?.settings;
      }

      setSalesState(prev => ({
        ...prev,
        // Fallback mirrors backend DEFAULT_SETTINGS (salesFollowUpFirestore.ts)
        settings: settings || {
          sourceEmails: ['sales@culturegenesis.com'],
          recipientEmails: ['james@culturegenesis.com'],
          daysBack: 21,
          enabled: true,
          internalDomains: ['culturegenesis.com', 'culturegenesis-partnerships.com'],
          maxThreadsToAnalyze: 50,
          excludePatterns: ['out of office', 'auto-reply', 'unsubscribe', 'automatic reply', 'do not reply'],
          minDaysSinceActivity: 3,
          sendEmptyResultEmail: true,
          suppressPreviouslyFlagged: true,
          suppressColdOutbound: true,
        },
        runs: runsRes?.runs || [],
        loading: false,
      }));
    } catch (err: any) {
      setSalesState(prev => ({
        ...prev,
        loading: false,
        error: err.message || 'Failed to load sales follow-up data',
      }));
    }
  }, []);

  // ============================================================
  // Network Report Data Loading
  // ============================================================
  const loadNetworkData = useCallback(async () => {
    setNetworkState(prev => ({ ...prev, loading: true, error: null }));
    try {
      const [settingsRes, runsRes] = await Promise.all([
        getNetworkReportSettings(),
        getNetworkReportRuns(10),
      ]);

      let settings = settingsRes?.settings;
      if (!settings) {
        await seedNetworkReportSettings();
        const seeded = await getNetworkReportSettings();
        settings = seeded?.settings;
      }

      setNetworkState(prev => ({
        ...prev,
        settings: settings || {
          recipientEmails: [],
          topN: 40,
          includeAllDef: true,
          enabled: true,
        },
        runs: runsRes?.runs || [],
        loading: false,
      }));
    } catch (err: any) {
      setNetworkState(prev => ({
        ...prev,
        loading: false,
        error: err.message || 'Failed to load network report data',
      }));
    }
  }, []);

  // ============================================================
  // Direct Sold Data Loading
  // ============================================================
  const loadDirectSoldData = useCallback(async () => {
    setDirectSoldState(prev => ({ ...prev, loading: true, error: null }));
    try {
      const [settingsRes, runsRes] = await Promise.all([
        getAdminSettings(),
        getProcessingRuns(10),
      ]);

      setDirectSoldState(prev => ({
        ...prev,
        settings: settingsRes?.settings || {
          schedule: { enabled: true },
          notifications: { emailOnComplete: true, emailOnFailure: true, notificationEmails: [] },
        },
        runs: runsRes?.runs || [],
        loading: false,
      }));
    } catch (err: any) {
      setDirectSoldState(prev => ({
        ...prev,
        loading: false,
        error: err.message || 'Failed to load direct sold data',
      }));
    }
  }, []);

  // ============================================================
  // RFP Inbox Scanner Data Loading
  // ============================================================
  const loadRfpData = useCallback(async () => {
    setRfpState(prev => ({ ...prev, loading: true, error: null }));
    try {
      const [settingsRes, runsRes] = await Promise.all([
        getRfpSettings(),
        getRfpRuns(10),
      ]);

      let settings = settingsRes?.settings;
      if (!settings) {
        await seedRfpSettings();
        const seeded = await getRfpSettings();
        settings = seeded?.settings;
      }

      setRfpState(prev => ({
        ...prev,
        settings: settings || {
          notificationRecipients: [],
          excludePatterns: [],
          searchTimeWindowHours: 4,
          maxRfpsPerRun: 10,
          salesInbox: 'sales@culturegenesis.com',
          enabled: true,
        },
        runs: runsRes?.runs || [],
        loading: false,
      }));
    } catch (err: any) {
      setRfpState(prev => ({
        ...prev,
        loading: false,
        error: err.message || 'Failed to load RFP scanner data',
      }));
    }
  }, []);

  // ============================================================
  // IO Approval Forwarder Data Loading
  // ============================================================
  const loadIoApprovalData = useCallback(async () => {
    setIoApprovalState(prev => ({ ...prev, loading: true, error: null }));
    try {
      const [settingsRes, runsRes] = await Promise.all([
        getIoApprovalSettings(),
        getIoApprovalRuns(10),
      ]);

      let settings = settingsRes?.settings;
      if (!settings) {
        await seedIoApprovalSettings();
        const seeded = await getIoApprovalSettings();
        settings = seeded?.settings;
      }

      setIoApprovalState(prev => ({
        ...prev,
        settings: settings || {
          notificationRecipients: [],
          subjectPatterns: [],
          excludePatterns: [],
          forwardedSubject: 'IO Approval - Needs Attention',
          salesInbox: 'sales@culturegenesis.com',
          lookbackHours: 24,
          maxEmailsPerRun: 25,
          maxAttachmentBytes: 10 * 1024 * 1024,
          enabled: true,
        },
        runs: runsRes?.runs || [],
        loading: false,
      }));
    } catch (err: any) {
      setIoApprovalState(prev => ({
        ...prev,
        loading: false,
        error: err.message || 'Failed to load IO Approval Forwarder data',
      }));
    }
  }, []);

  // ============================================================
  // Meeting Digest Data Loading
  // ============================================================
  const loadMeetingDigestData = useCallback(async () => {
    setMeetingDigestState(prev => ({ ...prev, loading: true, error: null }));
    try {
      const [settingsRes, runsRes] = await Promise.all([
        getMeetingDigestSettings(),
        getMeetingDigestRuns(10),
      ]);

      let settings = settingsRes?.settings;
      if (!settings) {
        await seedMeetingDigestSettings();
        const seeded = await getMeetingDigestSettings();
        settings = seeded?.settings;
      }

      setMeetingDigestState(prev => ({
        ...prev,
        settings: settings || {
          recipientEmails: ['james@culturegenesis.com', 'eden@culturegenesis.com'],
          participantEmails: ['james@culturegenesis.com', 'eden@culturegenesis.com'],
          executiveEmails: ['shaun@culturegenesis.com', 'fred@culturegenesis.com', 'rik@culturegenesis.com'],
          daysBack: 4,
          forceEnableSmartNotes: true,
          enabled: true,
        },
        runs: runsRes?.runs || [],
        loading: false,
      }));
    } catch (err: any) {
      setMeetingDigestState(prev => ({
        ...prev,
        loading: false,
        error: err.message || 'Failed to load meeting digest data',
      }));
    }
  }, []);

  // ============================================================
  // Executive Calendar Data Loading
  // ============================================================
  const loadExecutiveCalendarData = useCallback(async () => {
    setExecLoading(true);
    setExecError(null);
    try {
      let res = await listExecutiveCalendarTasks();
      if (!res.tasks || res.tasks.length === 0) {
        await seedExecutiveCalendarTasks();
        res = await listExecutiveCalendarTasks();
      }
      const tasks = res.tasks || [];
      setExecTasks(tasks);

      // Initialize per-task card state. Preserve any in-flight unsaved edits
      // so a reload doesn't clobber what the user is typing.
      setExecCardState(prev => {
        const next: typeof prev = {};
        for (const t of tasks) {
          const existing = prev[t.id];
          next[t.id] = {
            expanded: existing?.expanded ?? false,
            saving: false,
            triggering: false,
            message: null,
            error: null,
            runs: existing?.runs ?? [],
            expandedRunId: existing?.expandedRunId ?? null,
            edit: existing?.edit ?? { ...t },
            dryRun: existing?.dryRun ?? false,
          };
        }
        return next;
      });

      // Load runs for each task in parallel.
      const runsResults = await Promise.all(
        tasks.map(t => getExecutiveCalendarRuns(t.id, 10).catch(() => ({ runs: [] })))
      );
      setExecCardState(prev => {
        const next = { ...prev };
        tasks.forEach((t, i) => {
          if (next[t.id]) {
            next[t.id] = { ...next[t.id], runs: runsResults[i]?.runs || [] };
          }
        });
        return next;
      });
    } catch (err: any) {
      setExecError(err.message || 'Failed to load executive calendar tasks');
    } finally {
      setExecLoading(false);
    }
  }, []);

  useEffect(() => {
    loadSalesData();
    loadNetworkData();
    loadDirectSoldData();
    loadRfpData();
    loadIoApprovalData();
    loadMeetingDigestData();
    loadExecutiveCalendarData();
  }, [loadSalesData, loadNetworkData, loadDirectSoldData, loadRfpData, loadIoApprovalData, loadMeetingDigestData, loadExecutiveCalendarData]);

  // ============================================================
  // Sales Follow-Up Handlers
  // ============================================================

  const handleSalesSave = async () => {
    setSalesState(prev => ({ ...prev, saving: true, message: null, error: null }));
    try {
      await updateSalesFollowUpSettings({
        sourceEmails: salesState.settings.sourceEmails,
        recipientEmails: salesState.settings.recipientEmails,
        daysBack: salesState.settings.daysBack,
        enabled: salesState.settings.enabled,
        maxThreadsToAnalyze: salesState.settings.maxThreadsToAnalyze,
        excludePatterns: salesState.settings.excludePatterns,
        internalDomains: salesState.settings.internalDomains,
        minDaysSinceActivity: salesState.settings.minDaysSinceActivity,
        sendEmptyResultEmail: salesState.settings.sendEmptyResultEmail,
        suppressPreviouslyFlagged: salesState.settings.suppressPreviouslyFlagged,
        suppressColdOutbound: salesState.settings.suppressColdOutbound,
      });
      setSalesState(prev => ({ ...prev, saving: false, message: 'Settings saved.' }));
      setTimeout(() => setSalesState(prev => ({ ...prev, message: null })), 3000);
    } catch (err: any) {
      setSalesState(prev => ({ ...prev, saving: false, error: err.message }));
    }
  };

  const refreshSalesRuns = useCallback(async () => {
    try {
      const runsRes = await getSalesFollowUpRuns(10);
      setSalesState(prev => ({ ...prev, runs: runsRes?.runs || [] }));
    } catch { /* silent */ }
  }, []);

  const handleSalesTrigger = async () => {
    const recipients = (salesState.settings?.recipientEmails || []).join(', ') || 'the configured recipients';
    const pausedWarning = salesState.settings?.enabled === false
      ? ' NOTE: the scanner is currently paused — a manual run still executes.'
      : '';
    const confirmMsg = salesDryRun
      ? 'Run a DRY RUN of the sales follow-up scanner now? The email will be built and stored in run history for preview, but nothing will be sent.'
      : `Run the sales follow-up scanner now? This scans the configured inboxes and sends a REAL email to: ${recipients}.${pausedWarning}`;
    if (!window.confirm(confirmMsg)) return;
    setSalesState(prev => ({ ...prev, triggering: true, error: null }));
    try {
      await triggerSalesFollowUp({ dryRun: salesDryRun });
      setSalesState(prev => ({
        ...prev,
        triggering: false,
        message: salesDryRun
          ? 'Dry run started. Refresh runs in a few minutes, then open the preview.'
          : 'Scan started. Refresh runs in a few minutes.',
      }));
      setTimeout(() => setSalesState(prev => ({ ...prev, message: null })), 5000);
      // Auto-refresh runs after a short delay to pick up the new 'running' record
      setTimeout(refreshSalesRuns, 2000);
    } catch (err: any) {
      setSalesState(prev => ({ ...prev, triggering: false, error: err.message }));
    }
  };

  const handleSalesPreview = async (runId: string) => {
    try {
      const res = await getSalesFollowUpRun(runId);
      const html = res?.run?.previewHtml;
      if (!html) {
        window.alert('No preview stored for this run (previews are saved for dry runs only).');
        return;
      }
      const win = window.open('', '_blank');
      if (win) {
        win.document.write(html);
        win.document.close();
      }
    } catch (err: any) {
      window.alert(`Failed to load preview: ${err.message}`);
    }
  };

  const handleSalesPauseResume = async () => {
    setSalesState(prev => ({ ...prev, error: null }));
    try {
      if (salesState.settings?.enabled) {
        await pauseSalesFollowUp();
        setSalesState(prev => ({
          ...prev,
          settings: { ...prev.settings, enabled: false },
          message: 'Scanner paused.',
        }));
      } else {
        await resumeSalesFollowUp();
        setSalesState(prev => ({
          ...prev,
          settings: { ...prev.settings, enabled: true },
          message: 'Scanner resumed.',
        }));
      }
      setTimeout(() => setSalesState(prev => ({ ...prev, message: null })), 3000);
    } catch (err: any) {
      setSalesState(prev => ({ ...prev, error: err.message }));
    }
  };

  // ============================================================
  // Network Report Handlers
  // ============================================================

  const handleNetworkSave = async () => {
    setNetworkState(prev => ({ ...prev, saving: true, message: null, error: null }));
    try {
      await updateNetworkReportSettings({
        recipientEmails: networkState.settings.recipientEmails,
        topN: networkState.settings.topN,
        includeAllDef: networkState.settings.includeAllDef,
        enabled: networkState.settings.enabled,
      });
      setNetworkState(prev => ({ ...prev, saving: false, message: 'Settings saved.' }));
      setTimeout(() => setNetworkState(prev => ({ ...prev, message: null })), 3000);
    } catch (err: any) {
      setNetworkState(prev => ({ ...prev, saving: false, error: err.message }));
    }
  };

  const refreshNetworkRuns = useCallback(async () => {
    try {
      const runsRes = await getNetworkReportRuns(10);
      setNetworkState(prev => ({ ...prev, runs: runsRes?.runs || [] }));
    } catch { /* silent */ }
  }, []);

  const handleNetworkTrigger = async () => {
    if (!window.confirm('Generate and send the network performance report now?')) return;
    setNetworkState(prev => ({ ...prev, triggering: true, error: null }));
    try {
      await triggerNetworkReport();
      setNetworkState(prev => ({ ...prev, triggering: false, message: 'Report started. Refresh runs in a few minutes.' }));
      setTimeout(() => setNetworkState(prev => ({ ...prev, message: null })), 5000);
      setTimeout(refreshNetworkRuns, 2000);
    } catch (err: any) {
      setNetworkState(prev => ({ ...prev, triggering: false, error: err.message }));
    }
  };

  const handleNetworkPauseResume = async () => {
    setNetworkState(prev => ({ ...prev, error: null }));
    try {
      if (networkState.settings?.enabled) {
        await pauseNetworkReport();
        setNetworkState(prev => ({
          ...prev,
          settings: { ...prev.settings, enabled: false },
          message: 'Report paused.',
        }));
      } else {
        await resumeNetworkReport();
        setNetworkState(prev => ({
          ...prev,
          settings: { ...prev.settings, enabled: true },
          message: 'Report resumed.',
        }));
      }
      setTimeout(() => setNetworkState(prev => ({ ...prev, message: null })), 3000);
    } catch (err: any) {
      setNetworkState(prev => ({ ...prev, error: err.message }));
    }
  };

  // ============================================================
  // Meeting Digest Handlers
  // ============================================================

  const handleMeetingDigestSave = async () => {
    setMeetingDigestState(prev => ({ ...prev, saving: true, message: null, error: null }));
    try {
      await updateMeetingDigestSettings({
        recipientEmails: meetingDigestState.settings.recipientEmails,
        participantEmails: meetingDigestState.settings.participantEmails,
        executiveEmails: meetingDigestState.settings.executiveEmails || [],
        daysBack: meetingDigestState.settings.daysBack,
        forceEnableSmartNotes: meetingDigestState.settings.forceEnableSmartNotes,
        enabled: meetingDigestState.settings.enabled,
      });
      setMeetingDigestState(prev => ({ ...prev, saving: false, message: 'Settings saved.' }));
      setTimeout(() => setMeetingDigestState(prev => ({ ...prev, message: null })), 3000);
    } catch (err: any) {
      setMeetingDigestState(prev => ({ ...prev, saving: false, error: err.message }));
    }
  };

  const refreshMeetingDigestRuns = useCallback(async () => {
    try {
      const runsRes = await getMeetingDigestRuns(10);
      setMeetingDigestState(prev => ({ ...prev, runs: runsRes?.runs || [] }));
    } catch { /* silent */ }
  }, []);

  const handleMeetingDigestTrigger = async () => {
    const confirmMsg = meetingDryRun
      ? 'Run a DRY RUN of the meeting digest now? The digest will be generated and stored in run history, but no email will be sent.'
      : 'Generate and send the meeting digest now?';
    if (!window.confirm(confirmMsg)) return;
    setMeetingDigestState(prev => ({ ...prev, triggering: true, error: null }));
    try {
      await triggerMeetingDigest({ dryRun: meetingDryRun });
      setMeetingDigestState(prev => ({
        ...prev,
        triggering: false,
        message: meetingDryRun
          ? 'Dry run started. Refresh runs in a few minutes.'
          : 'Digest started. Refresh runs in a few minutes.',
      }));
      setTimeout(() => setMeetingDigestState(prev => ({ ...prev, message: null })), 5000);
      setTimeout(refreshMeetingDigestRuns, 2000);
    } catch (err: any) {
      setMeetingDigestState(prev => ({ ...prev, triggering: false, error: err.message }));
    }
  };

  const handleMeetingDigestPauseResume = async () => {
    setMeetingDigestState(prev => ({ ...prev, error: null }));
    try {
      if (meetingDigestState.settings?.enabled) {
        await pauseMeetingDigest();
        setMeetingDigestState(prev => ({
          ...prev,
          settings: { ...prev.settings, enabled: false },
          message: 'Digest paused.',
        }));
      } else {
        await resumeMeetingDigest();
        setMeetingDigestState(prev => ({
          ...prev,
          settings: { ...prev.settings, enabled: true },
          message: 'Digest resumed.',
        }));
      }
      setTimeout(() => setMeetingDigestState(prev => ({ ...prev, message: null })), 3000);
    } catch (err: any) {
      setMeetingDigestState(prev => ({ ...prev, error: err.message }));
    }
  };

  // ============================================================
  // Executive Calendar Handlers
  // ============================================================

  const updateExecCard = (
    taskId: string,
    patch: Partial<NonNullable<typeof execCardState[string]>>
  ) => {
    setExecCardState(prev => ({
      ...prev,
      [taskId]: { ...(prev[taskId] || ({} as any)), ...patch },
    }));
  };

  const updateExecEdit = (taskId: string, patch: Partial<ExecutiveCalendarTask>) => {
    setExecCardState(prev => {
      const card = prev[taskId];
      if (!card?.edit) return prev;
      return {
        ...prev,
        [taskId]: { ...card, edit: { ...card.edit, ...patch } },
      };
    });
  };

  const addExecChip = (
    taskId: string,
    field: 'executiveEmails' | 'recipientEmails',
    value: string,
    inputSetter: (next: Record<string, string>) => void,
    inputState: Record<string, string>,
  ) => {
    const trimmed = value.trim().toLowerCase();
    if (!trimmed) return;
    setExecCardState(prev => {
      const card = prev[taskId];
      if (!card?.edit) return prev;
      const list = card.edit[field] || [];
      if (list.includes(trimmed)) return prev;
      return {
        ...prev,
        [taskId]: {
          ...card,
          edit: { ...card.edit, [field]: [...list, trimmed] },
        },
      };
    });
    inputSetter({ ...inputState, [taskId]: '' });
  };

  const removeExecChip = (
    taskId: string,
    field: 'executiveEmails' | 'recipientEmails',
    value: string
  ) => {
    setExecCardState(prev => {
      const card = prev[taskId];
      if (!card?.edit) return prev;
      return {
        ...prev,
        [taskId]: {
          ...card,
          edit: {
            ...card.edit,
            [field]: (card.edit[field] || []).filter(v => v !== value),
          },
        },
      };
    });
  };

  const refreshExecRuns = async (taskId: string) => {
    try {
      const runsRes = await getExecutiveCalendarRuns(taskId, 10);
      updateExecCard(taskId, { runs: runsRes?.runs || [] });
    } catch { /* silent */ }
  };

  const handleExecSave = async (taskId: string) => {
    const card = execCardState[taskId];
    if (!card?.edit) return;
    const edit = card.edit;
    updateExecCard(taskId, { saving: true, message: null, error: null });
    try {
      const updated = await updateExecutiveCalendarTask(taskId, {
        name: edit.name,
        executiveEmails: edit.executiveEmails,
        recipientEmails: edit.recipientEmails,
        enabled: edit.enabled,
        scheduleCron: edit.scheduleCron,
        scheduleTimezone: edit.scheduleTimezone,
        daysAhead: edit.daysAhead,
      });
      setExecTasks(prev => prev.map(t => (t.id === taskId ? updated.task : t)));
      updateExecCard(taskId, { saving: false, message: 'Settings saved.', edit: { ...updated.task } });
      setTimeout(() => updateExecCard(taskId, { message: null }), 3000);
    } catch (err: any) {
      updateExecCard(taskId, { saving: false, error: err.message || 'Save failed' });
    }
  };

  const handleExecTrigger = async (taskId: string) => {
    const card = execCardState[taskId];
    const dryRun = card?.dryRun ?? false;
    const confirmMsg = dryRun
      ? 'Run a DRY RUN of this calendar report? No email will be sent.'
      : 'Generate and send the calendar report email now?';
    if (!window.confirm(confirmMsg)) return;
    updateExecCard(taskId, { triggering: true, error: null });
    try {
      await triggerExecutiveCalendarTask(taskId, { dryRun });
      updateExecCard(taskId, {
        triggering: false,
        message: dryRun ? 'Dry run started. Refresh runs in a moment.' : 'Run started. Refresh runs in a moment.',
      });
      setTimeout(() => updateExecCard(taskId, { message: null }), 5000);
      setTimeout(() => refreshExecRuns(taskId), 2000);
    } catch (err: any) {
      updateExecCard(taskId, { triggering: false, error: err.message });
    }
  };

  const handleExecPauseResume = async (taskId: string) => {
    const task = execTasks.find(t => t.id === taskId);
    if (!task) return;
    updateExecCard(taskId, { error: null });
    try {
      if (task.enabled) {
        await pauseExecutiveCalendarTask(taskId);
      } else {
        await resumeExecutiveCalendarTask(taskId);
      }
      const refreshed = await listExecutiveCalendarTasks();
      setExecTasks(refreshed.tasks || []);
      const updated = (refreshed.tasks || []).find(t => t.id === taskId);
      if (updated) {
        updateExecCard(taskId, {
          edit: { ...updated },
          message: updated.enabled ? 'Task resumed.' : 'Task paused.',
        });
      }
      setTimeout(() => updateExecCard(taskId, { message: null }), 3000);
    } catch (err: any) {
      updateExecCard(taskId, { error: err.message });
    }
  };

  const handleExecDelete = async (taskId: string) => {
    const task = execTasks.find(t => t.id === taskId);
    if (!task) return;
    if (!window.confirm(`Delete the executive calendar task "${task.name}"? This also removes its Cloud Scheduler job.`)) return;
    try {
      await deleteExecutiveCalendarTask(taskId);
      setExecTasks(prev => prev.filter(t => t.id !== taskId));
      setExecCardState(prev => {
        const next = { ...prev };
        delete next[taskId];
        return next;
      });
    } catch (err: any) {
      updateExecCard(taskId, { error: err.message });
    }
  };

  const handleExecCreate = async () => {
    if (!execNewDraft.name.trim()) {
      alert('Please enter a name for the task.');
      return;
    }
    if (execNewDraft.executiveEmails.length === 0) {
      alert('Add at least one executive email.');
      return;
    }
    if (execNewDraft.recipientEmails.length === 0) {
      alert('Add at least one recipient email.');
      return;
    }
    setExecCreating(true);
    try {
      await createExecutiveCalendarTask({
        name: execNewDraft.name.trim(),
        executiveEmails: execNewDraft.executiveEmails,
        recipientEmails: execNewDraft.recipientEmails,
        enabled: true,
        scheduleCron: execNewDraft.scheduleCron,
        scheduleTimezone: execNewDraft.scheduleTimezone,
        daysAhead: execNewDraft.daysAhead,
      });
      // Reset draft and reload.
      setExecNewDraft({
        name: '',
        executiveEmails: [],
        recipientEmails: [],
        scheduleCron: '0 6 * * 0',
        scheduleTimezone: 'America/Los_Angeles',
        daysAhead: 7,
      });
      setExecNewDraftExecInput('');
      setExecNewDraftRecipInput('');
      setExecShowNewForm(false);
      await loadExecutiveCalendarData();
    } catch (err: any) {
      alert(`Failed to create task: ${err.message}`);
    } finally {
      setExecCreating(false);
    }
  };

  // ============================================================
  // Direct Sold Handlers
  // ============================================================

  const handleDirectSoldSave = async () => {
    setDirectSoldState(prev => ({ ...prev, saving: true, message: null, error: null }));
    try {
      await updateAdminSettings({
        notifications: directSoldState.settings.notifications,
      });
      setDirectSoldState(prev => ({ ...prev, saving: false, message: 'Settings saved.' }));
      setTimeout(() => setDirectSoldState(prev => ({ ...prev, message: null })), 3000);
    } catch (err: any) {
      setDirectSoldState(prev => ({ ...prev, saving: false, error: err.message }));
    }
  };

  const refreshDirectSoldRuns = useCallback(async () => {
    try {
      const runsRes = await getProcessingRuns(10);
      setDirectSoldState(prev => ({ ...prev, runs: runsRes?.runs || [] }));
    } catch { /* silent */ }
  }, []);

  const handleDirectSoldTrigger = async () => {
    if (!window.confirm('Send a test upload-reminder email now? (Skipped if the month’s GAM report is already uploaded.)')) return;
    setDirectSoldState(prev => ({ ...prev, triggering: true, error: null }));
    try {
      const res = await sendDirectSoldReminderTest();
      setDirectSoldState(prev => ({ ...prev, triggering: false, message: res.message || 'Test reminder sent.' }));
      setTimeout(() => setDirectSoldState(prev => ({ ...prev, message: null })), 5000);
    } catch (err: any) {
      setDirectSoldState(prev => ({ ...prev, triggering: false, error: err.message }));
    }
  };

  const handleDirectSoldPauseResume = async () => {
    setDirectSoldState(prev => ({ ...prev, error: null }));
    try {
      if (directSoldState.settings?.schedule?.enabled) {
        await pauseDirectSold();
        setDirectSoldState(prev => ({
          ...prev,
          settings: {
            ...prev.settings,
            schedule: { ...prev.settings.schedule, enabled: false },
          },
          message: 'Reminder paused.',
        }));
      } else {
        await resumeDirectSold();
        setDirectSoldState(prev => ({
          ...prev,
          settings: {
            ...prev.settings,
            schedule: { ...prev.settings.schedule, enabled: true },
          },
          message: 'Reminder resumed.',
        }));
      }
      setTimeout(() => setDirectSoldState(prev => ({ ...prev, message: null })), 3000);
    } catch (err: any) {
      setDirectSoldState(prev => ({ ...prev, error: err.message }));
    }
  };

  // ============================================================
  // RFP Inbox Scanner Handlers
  // ============================================================

  const handleRfpSave = async () => {
    setRfpState(prev => ({ ...prev, saving: true, message: null, error: null }));
    try {
      await updateRfpSettings({
        notificationRecipients: rfpState.settings.notificationRecipients,
        excludePatterns: rfpState.settings.excludePatterns,
        searchTimeWindowHours: rfpState.settings.searchTimeWindowHours,
        maxRfpsPerRun: rfpState.settings.maxRfpsPerRun,
        enabled: rfpState.settings.enabled,
      });
      setRfpState(prev => ({ ...prev, saving: false, message: 'Settings saved.' }));
      setTimeout(() => setRfpState(prev => ({ ...prev, message: null })), 3000);
    } catch (err: any) {
      setRfpState(prev => ({ ...prev, saving: false, error: err.message }));
    }
  };

  const refreshRfpRuns = useCallback(async () => {
    try {
      const runsRes = await getRfpRuns(10);
      setRfpState(prev => ({ ...prev, runs: runsRes?.runs || [] }));
    } catch { /* silent */ }
  }, []);

  const handleRfpTrigger = async () => {
    const input = window.prompt(
      'Scan the sales inbox for RFPs now?\n\nSearch window (hours back from now).\nLeave blank to use saved setting (' + (rfpState.settings?.searchTimeWindowHours ?? 4) + 'h).',
      ''
    );
    if (input === null) return; // cancelled
    const hours = input.trim() ? parseInt(input.trim(), 10) : undefined;
    if (hours !== undefined && (isNaN(hours) || hours < 1 || hours > 48)) {
      alert('Search window must be between 1 and 48 hours.');
      return;
    }
    setRfpState(prev => ({ ...prev, triggering: true, error: null }));
    try {
      await triggerRfpProcessing(hours);
      const msg = hours
        ? `Scan started (searching last ${hours}h). Refresh runs in a few minutes.`
        : 'Scan started. Refresh runs in a few minutes.';
      setRfpState(prev => ({ ...prev, triggering: false, message: msg }));
      setTimeout(() => setRfpState(prev => ({ ...prev, message: null })), 5000);
      setTimeout(refreshRfpRuns, 2000);
    } catch (err: any) {
      setRfpState(prev => ({ ...prev, triggering: false, error: err.message }));
    }
  };

  const handleRfpPauseResume = async () => {
    setRfpState(prev => ({ ...prev, error: null }));
    try {
      if (rfpState.settings?.enabled) {
        await pauseRfpProcessing();
        setRfpState(prev => ({
          ...prev,
          settings: { ...prev.settings, enabled: false },
          message: 'Scanner paused.',
        }));
      } else {
        await resumeRfpProcessing();
        setRfpState(prev => ({
          ...prev,
          settings: { ...prev.settings, enabled: true },
          message: 'Scanner resumed.',
        }));
      }
      setTimeout(() => setRfpState(prev => ({ ...prev, message: null })), 3000);
    } catch (err: any) {
      setRfpState(prev => ({ ...prev, error: err.message }));
    }
  };

  // ============================================================
  // IO Approval Forwarder Handlers
  // ============================================================

  const handleIoApprovalSave = async () => {
    setIoApprovalState(prev => ({ ...prev, saving: true, message: null, error: null }));
    try {
      await updateIoApprovalSettings({
        notificationRecipients: ioApprovalState.settings.notificationRecipients,
        subjectPatterns: ioApprovalState.settings.subjectPatterns,
        excludePatterns: ioApprovalState.settings.excludePatterns,
        forwardedSubject: ioApprovalState.settings.forwardedSubject,
        lookbackHours: ioApprovalState.settings.lookbackHours,
        maxEmailsPerRun: ioApprovalState.settings.maxEmailsPerRun,
        enabled: ioApprovalState.settings.enabled,
      });
      setIoApprovalState(prev => ({ ...prev, saving: false, message: 'Settings saved.' }));
      setTimeout(() => setIoApprovalState(prev => ({ ...prev, message: null })), 3000);
    } catch (err: any) {
      setIoApprovalState(prev => ({ ...prev, saving: false, error: err.message }));
    }
  };

  const refreshIoApprovalRuns = useCallback(async () => {
    try {
      const runsRes = await getIoApprovalRuns(10);
      setIoApprovalState(prev => ({ ...prev, runs: runsRes?.runs || [] }));
    } catch { /* silent */ }
  }, []);

  const handleIoApprovalTrigger = async () => {
    const input = window.prompt(
      'Scan the sales inbox for IO approvals now?\n\nLookback window (hours back from now).\nLeave blank to use saved setting (' + (ioApprovalState.settings?.lookbackHours ?? 24) + 'h).',
      ''
    );
    if (input === null) return;
    const hours = input.trim() ? parseInt(input.trim(), 10) : undefined;
    if (hours !== undefined && (isNaN(hours) || hours < 1 || hours > 48)) {
      alert('Lookback window must be between 1 and 48 hours.');
      return;
    }
    setIoApprovalState(prev => ({ ...prev, triggering: true, error: null }));
    try {
      await triggerIoApprovalProcessing(hours);
      const msg = hours
        ? `Scan started (searching last ${hours}h). Refresh runs in a few minutes.`
        : 'Scan started. Refresh runs in a few minutes.';
      setIoApprovalState(prev => ({ ...prev, triggering: false, message: msg }));
      setTimeout(() => setIoApprovalState(prev => ({ ...prev, message: null })), 5000);
      setTimeout(refreshIoApprovalRuns, 2000);
    } catch (err: any) {
      setIoApprovalState(prev => ({ ...prev, triggering: false, error: err.message }));
    }
  };

  const handleIoApprovalPauseResume = async () => {
    setIoApprovalState(prev => ({ ...prev, error: null }));
    try {
      if (ioApprovalState.settings?.enabled) {
        await pauseIoApprovalProcessing();
        setIoApprovalState(prev => ({
          ...prev,
          settings: { ...prev.settings, enabled: false },
          message: 'Forwarder paused.',
        }));
      } else {
        await resumeIoApprovalProcessing();
        setIoApprovalState(prev => ({
          ...prev,
          settings: { ...prev.settings, enabled: true },
          message: 'Forwarder resumed.',
        }));
      }
      setTimeout(() => setIoApprovalState(prev => ({ ...prev, message: null })), 3000);
    } catch (err: any) {
      setIoApprovalState(prev => ({ ...prev, error: err.message }));
    }
  };

  // ============================================================
  // Chip list helpers
  // ============================================================

  const addChip = (
    listKey: string,
    value: string,
    setter: React.Dispatch<React.SetStateAction<TaskCardState>>,
    inputSetter: React.Dispatch<React.SetStateAction<string>>
  ) => {
    const trimmed = value.trim().toLowerCase();
    if (!trimmed) return;
    setter(prev => {
      const list: string[] = prev.settings?.[listKey] || [];
      if (list.includes(trimmed)) return prev;
      return {
        ...prev,
        settings: { ...prev.settings, [listKey]: [...list, trimmed] },
      };
    });
    inputSetter('');
  };

  const removeChip = (
    listKey: string,
    value: string,
    setter: React.Dispatch<React.SetStateAction<TaskCardState>>
  ) => {
    setter(prev => ({
      ...prev,
      settings: {
        ...prev.settings,
        [listKey]: (prev.settings?.[listKey] || []).filter((v: string) => v !== value),
      },
    }));
  };

  // Direct sold nested chip helpers
  const addDirectSoldRecipient = (value: string) => {
    const trimmed = value.trim().toLowerCase();
    if (!trimmed) return;
    setDirectSoldState(prev => {
      const emails: string[] = prev.settings?.notifications?.notificationEmails || [];
      if (emails.includes(trimmed)) return prev;
      return {
        ...prev,
        settings: {
          ...prev.settings,
          notifications: {
            ...prev.settings.notifications,
            notificationEmails: [...emails, trimmed],
          },
        },
      };
    });
    setDirectSoldNewRecipient('');
  };

  const removeDirectSoldRecipient = (value: string) => {
    setDirectSoldState(prev => ({
      ...prev,
      settings: {
        ...prev.settings,
        notifications: {
          ...prev.settings.notifications,
          notificationEmails: (prev.settings?.notifications?.notificationEmails || []).filter((v: string) => v !== value),
        },
      },
    }));
  };

  // ============================================================
  // Render helpers
  // ============================================================

  // null = settings not loaded — show a neutral badge instead of failing open to "Active"
  const renderStatusBadge = (enabled: boolean | null) => (
    <span style={{
      display: 'inline-block',
      padding: '2px 10px',
      borderRadius: '12px',
      fontSize: '11px',
      fontWeight: 600,
      textTransform: 'uppercase' as const,
      letterSpacing: '0.3px',
      background: enabled === null ? 'var(--bg-app)' : enabled ? 'var(--success-soft)' : 'var(--danger-soft)',
      color: enabled === null ? 'var(--fg-3)' : enabled ? 'var(--success)' : 'var(--danger)',
    }}>
      {enabled === null ? '—' : enabled ? 'Active' : 'Paused'}
    </span>
  );

  const renderRunStatusBadge = (status: string) => (
    <span style={{
      display: 'inline-block',
      padding: '2px 8px',
      borderRadius: '12px',
      fontSize: '11px',
      fontWeight: 600,
      textTransform: 'uppercase' as const,
      letterSpacing: '0.3px',
      background: status === 'completed' ? 'var(--success-soft)'
        : status === 'running' ? 'var(--info-soft)'
        : status === 'skipped' ? 'var(--bg-app)'
        : 'var(--danger-soft)',
      color: status === 'completed' ? 'var(--success)'
        : status === 'running' ? 'var(--accent)'
        : status === 'skipped' ? 'var(--fg-3)'
        : 'var(--danger)',
    }}>
      {status}
    </span>
  );

  const renderChipList = (
    items: string[],
    onRemove: (item: string) => void
  ) => (
    <div style={styles.chipList}>
      {items.map(item => (
        <div key={item} style={styles.chip}>
          <span>{item}</span>
          <button onClick={() => onRemove(item)} style={styles.chipRemove}>&times;</button>
        </div>
      ))}
    </div>
  );

  const renderAddInput = (
    value: string,
    onChange: (v: string) => void,
    onAdd: () => void,
    placeholder: string,
    type: string = 'email'
  ) => (
    <div style={styles.addRow}>
      <input
        style={{ ...styles.input, flex: 1 }}
        type={type}
        placeholder={placeholder}
        value={value}
        onChange={e => onChange(e.target.value)}
        onKeyDown={e => e.key === 'Enter' && onAdd()}
      />
      <button onClick={onAdd} style={styles.addButton}>Add</button>
    </div>
  );

  const renderRunHistory = (
    runs: any[],
    expandedRunId: string | null,
    setExpandedRunId: (id: string | null) => void,
    renderDetails: (run: any) => React.ReactNode,
    onRefresh?: () => void
  ) => {
    if (runs.length === 0) {
      return (
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <p style={styles.hint}>No runs yet.</p>
          {onRefresh && (
            <button onClick={onRefresh} style={styles.refreshButton}>Refresh</button>
          )}
        </div>
      );
    }

    return (
      <div style={{ overflowX: 'auto' as const }}>
        {onRefresh && (
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '8px' }}>
            <button onClick={onRefresh} style={styles.refreshButton}>Refresh</button>
          </div>
        )}
        <table style={styles.table}>
          <thead>
            <tr>
              <th style={styles.th}>Status</th>
              <th style={styles.th}>Started</th>
              <th style={styles.th}>Duration</th>
              <th style={styles.th}>Triggered By</th>
              <th style={styles.th}></th>
            </tr>
          </thead>
          <tbody>
            {runs.map(run => (
              <React.Fragment key={run.id}>
                <tr
                  style={{ cursor: 'pointer' }}
                  onClick={() => setExpandedRunId(expandedRunId === run.id ? null : run.id)}
                >
                  <td style={styles.td}>{renderRunStatusBadge(run.status)}</td>
                  <td style={styles.td}>{formatDate(run.startedAt)}</td>
                  <td style={styles.td}>{formatDuration(run.durationMs)}</td>
                  <td style={styles.td}>{run.triggeredBy}</td>
                  <td style={styles.td}>
                    <span style={{ fontSize: '12px', color: 'var(--fg-3)' }}>
                      {expandedRunId === run.id ? '\u25B2' : '\u25BC'}
                    </span>
                  </td>
                </tr>
                {expandedRunId === run.id && (
                  <tr>
                    <td colSpan={5} style={{ padding: '12px 16px', background: 'var(--bg-app)' }}>
                      <div style={{ fontSize: '13px', color: 'var(--fg-2)', lineHeight: 1.6 }}>
                        <strong>Run ID:</strong> {run.id}<br />
                        <strong>Completed:</strong> {formatDate(run.completedAt)}<br />
                        {renderDetails(run)}
                        {run.error && (
                          <><strong>Error:</strong> <span style={{ color: 'var(--danger)' }}>{run.error}</span><br /></>
                        )}
                      </div>
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  // ============================================================
  // Task Card Wrapper
  // ============================================================

  const renderTaskCard = (
    title: string,
    schedule: string,
    description: string,
    state: TaskCardState,
    onToggleExpand: () => void,
    onPauseResume: () => void,
    onTrigger: () => void,
    triggerLabel: string,
    isEnabled: boolean | null,
    configContent: React.ReactNode,
    runHistoryContent: React.ReactNode
  ) => (
    <div style={styles.card}>
      {/* Header */}
      <div
        style={{
          ...styles.cardHeader,
          ...(state.expanded ? {} : { borderBottom: 'none' }),
        }}
        onClick={onToggleExpand}
      >
        <div style={styles.cardHeaderLeft}>
          <span style={{ fontSize: '14px', color: 'var(--fg-3)', marginRight: '8px' }}>
            {state.expanded ? '\u25BC' : '\u25B6'}
          </span>
          <h3 style={styles.cardTitle}>{title}</h3>
          {renderStatusBadge(isEnabled)}
          <span style={styles.scheduleText}>{schedule}</span>
        </div>
        <div style={styles.cardHeaderRight} onClick={e => e.stopPropagation()}>
          <button onClick={onPauseResume} style={styles.headerButton}>
            {isEnabled === false ? 'Resume' : 'Pause'}
          </button>
          <button
            onClick={onTrigger}
            disabled={state.triggering}
            style={{
              ...styles.headerButtonPrimary,
              ...(state.triggering ? styles.buttonDisabled : {}),
            }}
          >
            {state.triggering ? 'Starting...' : triggerLabel}
          </button>
        </div>
      </div>

      {/* Messages */}
      {state.error && <p style={styles.errorText}>{state.error}</p>}
      {state.message && <p style={styles.successText}>{state.message}</p>}

      {/* Description (always visible) */}
      <div style={{ ...styles.cardDescription, paddingBottom: state.expanded ? 0 : '16px' }}>
        {description}
      </div>

      {/* Expandable body */}
      {state.expanded && (
        <>
          {state.loading ? (
            <p style={{ ...styles.hint, padding: '16px 24px' }}>Loading...</p>
          ) : (
            <>
              {/* Configuration */}
              <div style={styles.cardSection}>
                {configContent}
              </div>

              {/* Run History */}
              <div style={styles.cardSection}>
                <h4 style={styles.sectionTitle}>Run History (last 10)</h4>
                {runHistoryContent}
              </div>
            </>
          )}
        </>
      )}
    </div>
  );

  // ============================================================
  // Render
  // ============================================================

  return (
    <div>
      {/* ==================== Sales Follow-Up ==================== */}
      {renderTaskCard(
        'Sales Follow-Up Scanner',
        'Mon 7 AM PT',
        'Scans the sales inbox weekly for missed follow-ups, oldest first. Identifies threads where external contacts never replied (Type A — nudge them) or where CG owes a reply (Type B), then emails a summary with draft replies to the recipients below. New inbound leads (brands wanting to buy or partner, nobody has replied yet) are listed in their own section; cold outreach, solicitations, and media-kit notifications are dismissed silently — dismissal counts and subjects appear in run history. Threads flagged in the previous email are suppressed until there\'s new activity.',
        salesState,
        () => setSalesState(prev => ({ ...prev, expanded: !prev.expanded })),
        handleSalesPauseResume,
        handleSalesTrigger,
        salesDryRun ? 'Run Dry' : 'Run Now',
        salesState.settings ? (salesState.settings.enabled ?? true) : null,
        // Config content
        <>
          {/* Source Emails */}
          <label style={styles.label}>Source Emails</label>
          <p style={styles.hint}>Inboxes to scan for missed follow-ups.</p>
          {renderChipList(
            salesState.settings?.sourceEmails || [],
            (email) => removeChip('sourceEmails', email, setSalesState)
          )}
          {renderAddInput(
            salesNewSource,
            setSalesNewSource,
            () => addChip('sourceEmails', salesNewSource, setSalesState, setSalesNewSource),
            'Add source email...'
          )}

          {/* Recipients */}
          <label style={{ ...styles.label, marginTop: '16px' }}>Report Recipients</label>
          <p style={styles.hint}>Who receives the follow-up scan results.</p>
          {renderChipList(
            salesState.settings?.recipientEmails || [],
            (email) => removeChip('recipientEmails', email, setSalesState)
          )}
          {renderAddInput(
            salesNewRecipient,
            setSalesNewRecipient,
            () => addChip('recipientEmails', salesNewRecipient, setSalesState, setSalesNewRecipient),
            'Add recipient email...'
          )}

          {/* Config fields */}
          <div style={{ ...styles.fieldsGrid, marginTop: '16px' }}>
            <div style={styles.fieldGroup}>
              <label style={styles.label}>Days Back</label>
              <input
                style={{ ...styles.input, maxWidth: '120px' }}
                type="number"
                min={1}
                max={60}
                value={salesState.settings?.daysBack ?? 21}
                onChange={e => setSalesState(prev => ({
                  ...prev,
                  settings: { ...prev.settings, daysBack: parseInt(e.target.value) || 21 },
                }))}
              />
              <p style={styles.hint}>Scan window — threads quiet longer than this are invisible to the scanner.</p>
            </div>
            <div style={styles.fieldGroup}>
              <label style={styles.label}>Min Days Stale</label>
              <input
                style={{ ...styles.input, maxWidth: '120px' }}
                type="number"
                min={0}
                max={30}
                value={salesState.settings?.minDaysSinceActivity ?? 3}
                onChange={e => setSalesState(prev => ({
                  ...prev,
                  settings: { ...prev.settings, minDaysSinceActivity: parseInt(e.target.value) || 0 },
                }))}
              />
              <p style={styles.hint}>Only flag threads with no activity for at least this many days.</p>
            </div>
            <div style={styles.fieldGroup}>
              <label style={styles.label}>Max Threads</label>
              <input
                style={{ ...styles.input, maxWidth: '120px' }}
                type="number"
                min={5}
                max={200}
                value={salesState.settings?.maxThreadsToAnalyze ?? 50}
                onChange={e => setSalesState(prev => ({
                  ...prev,
                  settings: { ...prev.settings, maxThreadsToAnalyze: parseInt(e.target.value) || 50 },
                }))}
              />
            </div>
          </div>

          {/* Behavior toggles */}
          <div style={{ display: 'flex', flexDirection: 'column' as const, gap: '8px', marginTop: '16px' }}>
            <label style={styles.checkboxLabel}>
              <input
                type="checkbox"
                checked={salesState.settings?.sendEmptyResultEmail ?? true}
                onChange={e => setSalesState(prev => ({
                  ...prev,
                  settings: { ...prev.settings, sendEmptyResultEmail: e.target.checked },
                }))}
              />
              <span>Send an "all clear" email when nothing is flagged (so silence never means "broken")</span>
            </label>
            <label style={styles.checkboxLabel}>
              <input
                type="checkbox"
                checked={salesState.settings?.suppressPreviouslyFlagged ?? true}
                onChange={e => setSalesState(prev => ({
                  ...prev,
                  settings: { ...prev.settings, suppressPreviouslyFlagged: e.target.checked },
                }))}
              />
              <span>Suppress threads already flagged in the previous email until there's new activity</span>
            </label>
            <label style={styles.checkboxLabel}>
              <input
                type="checkbox"
                checked={salesState.settings?.suppressColdOutbound ?? true}
                onChange={e => setSalesState(prev => ({
                  ...prev,
                  settings: { ...prev.settings, suppressColdOutbound: e.target.checked },
                }))}
              />
              <span>Skip cold outbound threads — emails a CG rep sent where no outside party has ever replied (follow-up paused; listed in Run History)</span>
            </label>
          </div>

          {/* Exclude Patterns */}
          <label style={{ ...styles.label, marginTop: '16px' }}>Exclude Patterns</label>
          <p style={styles.hint}>Threads matching these patterns are skipped. Built-in patterns always apply and can't be removed: "new media kit request", "media kit &amp; deal ids".</p>
          {renderChipList(
            salesState.settings?.excludePatterns || [],
            (pattern) => removeChip('excludePatterns', pattern, setSalesState)
          )}
          {renderAddInput(
            salesNewExclude,
            setSalesNewExclude,
            () => addChip('excludePatterns', salesNewExclude, setSalesState, setSalesNewExclude),
            'Add exclude pattern...',
            'text'
          )}

          {/* Internal Domains */}
          <label style={{ ...styles.label, marginTop: '16px' }}>Internal Domains</label>
          <p style={styles.hint}>Email domains treated as Culture Genesis when deciding who sent last (bare domain, e.g. culturegenesis.com).</p>
          {renderChipList(
            salesState.settings?.internalDomains || [],
            (domain) => removeChip('internalDomains', domain, setSalesState)
          )}
          {renderAddInput(
            salesNewInternalDomain,
            setSalesNewInternalDomain,
            () => addChip('internalDomains', salesNewInternalDomain, setSalesState, setSalesNewInternalDomain),
            'Add internal domain...',
            'text'
          )}

          {/* Save + Dry Run */}
          <div style={{ ...styles.saveRow, justifyContent: 'space-between', alignItems: 'center' }}>
            <label style={styles.checkboxLabel}>
              <input
                type="checkbox"
                checked={salesDryRun}
                onChange={e => setSalesDryRun(e.target.checked)}
              />
              <span>Dry run on next "Run Now" (no email sent — preview stored in run history)</span>
            </label>
            <button
              onClick={handleSalesSave}
              disabled={salesState.saving}
              style={{ ...styles.saveButton, ...(salesState.saving ? styles.buttonDisabled : {}) }}
            >
              {salesState.saving ? 'Saving...' : 'Save Settings'}
            </button>
          </div>
        </>,
        // Run history content
        renderRunHistory(
          salesState.runs,
          salesState.expandedRunId,
          (id) => setSalesState(prev => ({ ...prev, expandedRunId: id })),
          (run) => (
            <>
              {run.skipReason && (
                <><strong>Skipped:</strong> {run.skipReason}<br /></>
              )}
              {run.stats && (
                <>
                  <strong>Threads Scanned:</strong> {run.stats.totalThreadsScanned ?? '-'}<br />
                  <strong>Analyzed:</strong> {run.stats.threadsAnalyzed ?? '-'}
                  {run.stats.analysisFailedCount ? <> | <strong style={{ color: 'var(--danger)' }}>Failed:</strong> {run.stats.analysisFailedCount}</> : null}
                  {run.stats.skippedFreshCount ? <> | <strong>Too fresh:</strong> {run.stats.skippedFreshCount}</> : null}
                  {run.stats.suppressedCount ? <> | <strong>Suppressed:</strong> {run.stats.suppressedCount}</> : null}
                  {run.stats.dismissedCount ? (
                    <> | <strong>Dismissed:</strong> {run.stats.dismissedCount}
                      {run.stats.dismissedByCategory
                        ? ` (${Object.entries(run.stats.dismissedByCategory)
                            .map(([cat, n]) => `${String(cat).replace(/_/g, ' ')} ${n}`)
                            .join(' · ')})`
                        : ''}
                    </>
                  ) : null}
                  {run.stats.excludedByPatternCount ? <> | <strong>Excluded by pattern:</strong> {run.stats.excludedByPatternCount}</> : null}
                  {run.stats.coldOutboundCount ? <> | <strong>Cold outbound skipped:</strong> {run.stats.coldOutboundCount}</> : null}
                  {run.stats.suppressedDismissedCount ? <> | <strong>Previously dismissed:</strong> {run.stats.suppressedDismissedCount}</> : null}
                  <br />
                  <strong>Type A (awaiting their reply):</strong> {run.stats.typeACount ?? '-'}<br />
                  <strong>Type B (we owe a reply):</strong> {run.stats.typeBCount ?? '-'}<br />
                  <strong>New Leads (Type D):</strong> {run.stats.typeDCount ?? 0}<br />
                  <strong>Email Sent To:</strong> {run.stats.emailSentTo?.join(', ') || (run.stats.dryRun ? '(dry run — not sent)' : 'None')}<br />
                  {run.stats.dryRun && (
                    <>
                      <strong>Mode:</strong> DRY RUN{' '}
                      <button
                        onClick={(e) => { e.stopPropagation(); handleSalesPreview(run.id); }}
                        style={styles.refreshButton}
                      >
                        Preview email
                      </button>
                      <br />
                    </>
                  )}
                  {run.dismissedThreads?.length ? (
                    <span style={{ color: 'var(--fg-4)' }}>
                      <strong>Dismissed threads:</strong>{' '}
                      {run.dismissedThreads
                        .slice(0, 10)
                        .map((t: any) => `${t.subject} (${String(t.category).replace(/_/g, ' ')})`)
                        .join(' · ')}
                      {run.dismissedThreads.length > 10 ? ` · +${run.dismissedThreads.length - 10} more` : ''}
                      <br />
                    </span>
                  ) : null}
                  {run.coldOutboundThreads?.length ? (
                    <span style={{ color: 'var(--fg-4)' }}>
                      <strong>Cold outbound threads (skipped, no reply yet):</strong>{' '}
                      {run.coldOutboundThreads
                        .slice(0, 10)
                        .map((t: any) => (t.lastSenderEmail ? `${t.subject} (${String(t.lastSenderEmail).split('@')[0]})` : t.subject))
                        .join(' · ')}
                      {run.coldOutboundThreads.length > 10 ? ` · +${run.coldOutboundThreads.length - 10} more` : ''}
                      <br />
                    </span>
                  ) : null}
                </>
              )}
            </>
          ),
          refreshSalesRuns
        )
      )}

      {/* ==================== Network Performance Report ==================== */}
      {renderTaskCard(
        'Network Performance Report',
        '5th of month, 6 AM ET',
        'Generates a monthly channel performance report comparing YouTube analytics across the CG network. Ranks top channels by views and revenue, then emails the report to the recipients below.',
        networkState,
        () => setNetworkState(prev => ({ ...prev, expanded: !prev.expanded })),
        handleNetworkPauseResume,
        handleNetworkTrigger,
        'Run Now',
        networkState.settings?.enabled ?? true,
        // Config content
        <>
          {/* Recipients */}
          <label style={styles.label}>Report Recipients</label>
          <p style={styles.hint}>Who receives the monthly performance report email.</p>
          {renderChipList(
            networkState.settings?.recipientEmails || [],
            (email) => removeChip('recipientEmails', email, setNetworkState)
          )}
          {renderAddInput(
            networkNewRecipient,
            setNetworkNewRecipient,
            () => addChip('recipientEmails', networkNewRecipient, setNetworkState, setNetworkNewRecipient),
            'Add recipient email...'
          )}

          {/* Config fields */}
          <div style={{ ...styles.fieldsGrid, marginTop: '16px' }}>
            <div style={styles.fieldGroup}>
              <label style={styles.label}>Top Channels</label>
              <input
                style={{ ...styles.input, maxWidth: '120px' }}
                type="number"
                min={1}
                max={162}
                value={networkState.settings?.topN ?? 40}
                onChange={e => setNetworkState(prev => ({
                  ...prev,
                  settings: { ...prev.settings, topN: parseInt(e.target.value) || 40 },
                }))}
              />
              <span style={styles.hint}>Number of top channels to include</span>
            </div>
            <div style={styles.fieldGroup}>
              <label style={styles.label}>Include All DEF Channels</label>
              <label style={styles.checkboxLabel}>
                <input
                  type="checkbox"
                  checked={networkState.settings?.includeAllDef ?? true}
                  onChange={e => setNetworkState(prev => ({
                    ...prev,
                    settings: { ...prev.settings, includeAllDef: e.target.checked },
                  }))}
                />
                <span>Include All Def CMS channels</span>
              </label>
            </div>
          </div>

          {/* Save */}
          <div style={styles.saveRow}>
            <button
              onClick={handleNetworkSave}
              disabled={networkState.saving}
              style={{ ...styles.saveButton, ...(networkState.saving ? styles.buttonDisabled : {}) }}
            >
              {networkState.saving ? 'Saving...' : 'Save Settings'}
            </button>
          </div>
        </>,
        // Run history content
        renderRunHistory(
          networkState.runs,
          networkState.expandedRunId,
          (id) => setNetworkState(prev => ({ ...prev, expandedRunId: id })),
          (run) => (
            <>
              {run.stats && (
                <>
                  <strong>Channels Reported:</strong> {run.stats.channelsReported ?? '-'}<br />
                  <strong>Email Sent To:</strong> {run.stats.emailSentTo?.join(', ') || 'None'}<br />
                </>
              )}
            </>
          ),
          refreshNetworkRuns
        )
      )}

      {/* ==================== Meeting Digest ==================== */}
      {renderTaskCard(
        'Meeting Digest',
        'Friday 7 AM PT',
        'Pulls Google Meet recordings, Gemini smart notes, and transcripts for every email in the Participating Users list via domain-wide delegation. Dedupes by meeting, summarizes via Claude into a single executive overview with action items, deals, and follow-ups for the Chief of Staff. Looks back over the past week of meetings (one digest per Friday).',
        meetingDigestState,
        () => setMeetingDigestState(prev => ({ ...prev, expanded: !prev.expanded })),
        handleMeetingDigestPauseResume,
        handleMeetingDigestTrigger,
        meetingDryRun ? 'Run Dry' : 'Run Now',
        meetingDigestState.settings?.enabled ?? true,
        // Config content
        <>
          {/* Recipients */}
          <label style={styles.label}>Digest Recipients</label>
          <p style={styles.hint}>Who receives the meeting digest email (Chief of Staff + leadership).</p>
          {renderChipList(
            meetingDigestState.settings?.recipientEmails || [],
            (email) => removeChip('recipientEmails', email, setMeetingDigestState)
          )}
          {renderAddInput(
            meetingNewRecipient,
            setMeetingNewRecipient,
            () => addChip('recipientEmails', meetingNewRecipient, setMeetingDigestState, setMeetingNewRecipient),
            'Add recipient email...'
          )}

          {/* Config fields */}
          <div style={{ ...styles.fieldsGrid, marginTop: '16px' }}>
            <div style={styles.fieldGroup}>
              <label style={styles.label}>Days Back</label>
              <input
                style={{ ...styles.input, maxWidth: '120px' }}
                type="number"
                min={1}
                max={30}
                value={meetingDigestState.settings?.daysBack ?? 4}
                onChange={e => setMeetingDigestState(prev => ({
                  ...prev,
                  settings: { ...prev.settings, daysBack: parseInt(e.target.value) || 4 },
                }))}
              />
              <span style={styles.hint}>7 covers the prior week between Friday runs</span>
            </div>
            <div style={styles.fieldGroup}>
              <label style={styles.label}>Force-enable Smart Notes</label>
              <label style={styles.checkboxLabel}>
                <input
                  type="checkbox"
                  checked={meetingDigestState.settings?.forceEnableSmartNotes ?? true}
                  onChange={e => setMeetingDigestState(prev => ({
                    ...prev,
                    settings: { ...prev.settings, forceEnableSmartNotes: e.target.checked },
                  }))}
                />
                <span>Turn on Gemini "Take notes for me" for spaces (forward-looking; requires Gemini license per user)</span>
              </label>
            </div>
          </div>

          {/* Participating Users */}
          <label style={{ ...styles.label, marginTop: '16px' }}>Participating Users</label>
          <p style={styles.hint}>
            Whose Meet history is scanned. Add @culturegenesis.com team emails — only meetings these users attended will appear in the digest.
            Required (at least one).
          </p>
          {renderChipList(
            meetingDigestState.settings?.participantEmails || [],
            (email) => removeChip('participantEmails', email, setMeetingDigestState)
          )}
          {renderAddInput(
            meetingNewParticipant,
            setMeetingNewParticipant,
            () => addChip('participantEmails', meetingNewParticipant, setMeetingDigestState, setMeetingNewParticipant),
            'Add team member email...'
          )}

          {/* Executive Calendars */}
          <label style={{ ...styles.label, marginTop: '16px' }}>Executive Calendars (Follow-up Suggestions)</label>
          <p style={styles.hint}>
            Calendars read to suggest follow-up meeting times when action items require executive involvement. Leave empty to disable follow-up suggestions.
          </p>
          {renderChipList(
            meetingDigestState.settings?.executiveEmails || [],
            (email) => removeChip('executiveEmails', email, setMeetingDigestState)
          )}
          {renderAddInput(
            meetingNewExecutive,
            setMeetingNewExecutive,
            () => addChip('executiveEmails', meetingNewExecutive, setMeetingDigestState, setMeetingNewExecutive),
            'Add executive email...'
          )}

          {/* Save + Dry Run */}
          <div style={{ ...styles.saveRow, justifyContent: 'space-between', alignItems: 'center' }}>
            <label style={styles.checkboxLabel}>
              <input
                type="checkbox"
                checked={meetingDryRun}
                onChange={e => setMeetingDryRun(e.target.checked)}
              />
              <span>Dry run on next "Run Now" (no email sent — preview only)</span>
            </label>
            <button
              onClick={handleMeetingDigestSave}
              disabled={meetingDigestState.saving}
              style={{ ...styles.saveButton, ...(meetingDigestState.saving ? styles.buttonDisabled : {}) }}
            >
              {meetingDigestState.saving ? 'Saving...' : 'Save Settings'}
            </button>
          </div>
        </>,
        // Run history content
        renderRunHistory(
          meetingDigestState.runs,
          meetingDigestState.expandedRunId,
          (id) => setMeetingDigestState(prev => ({ ...prev, expandedRunId: id })),
          (run) => (
            <>
              {run.stats && (
                <>
                  <strong>Users Scanned:</strong> {run.stats.totalUsersScanned ?? '-'}<br />
                  <strong>Total Meetings:</strong> {run.stats.totalMeetings ?? '-'} | <strong>With Notes:</strong> {run.stats.meetingsWithContent ?? '-'} | <strong>No Notes:</strong> {run.stats.meetingsWithoutContent ?? '-'}<br />
                  <strong>Smart Notes Enabled:</strong> {run.stats.smartNotesEnabledCount ?? '-'}<br />
                  <strong>Email Sent To:</strong> {run.stats.emailSentTo?.join(', ') || (run.stats.dryRun ? '(dry run — not sent)' : 'None')}<br />
                  {run.stats.dryRun && <><strong>Mode:</strong> DRY RUN<br /></>}
                </>
              )}
            </>
          ),
          refreshMeetingDigestRuns
        )
      )}

      {/* ==================== Executive Calendar Schedules ==================== */}
      <div style={{ marginTop: '32px', marginBottom: '8px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <h3 style={{ margin: 0, fontSize: '14px', fontWeight: 600, color: 'var(--fg-1)', textTransform: 'uppercase' as const, letterSpacing: '0.5px' }}>
          Executive Calendar Schedules
        </h3>
        <button
          onClick={() => setExecShowNewForm(v => !v)}
          style={styles.headerButton}
        >
          {execShowNewForm ? 'Cancel' : '+ Add New Task'}
        </button>
      </div>
      <p style={{ ...styles.hint, marginTop: 0, marginBottom: '16px', maxWidth: '780px' }}>
        Independent of the Meeting Digest above. Each task reads the upcoming-week calendar for one or more executives via the service account (domain-wide delegation, read-only) and emails the rendered day-by-day schedule to a designated assistant or chief of staff. Use this to give each executive's support staff a clean weekly briefing of meetings ahead. Add one task per executive-to-recipient mapping; each has its own cron schedule, look-ahead window, and recipient list.
      </p>

      {execError && <p style={styles.errorText}>{execError}</p>}
      {execLoading && <p style={{ ...styles.hint, padding: '0 0 12px' }}>Loading executive calendar tasks…</p>}

      {execShowNewForm && (
        <div style={{ ...styles.card, padding: '20px 24px' }}>
          <h4 style={{ ...styles.sectionTitle, marginTop: 0 }}>New Calendar Schedule Task</h4>

          <label style={styles.label}>Task Name</label>
          <input
            style={{ ...styles.input, width: '100%', maxWidth: '500px' }}
            type="text"
            placeholder="e.g. Shaun + Fred → Eden"
            value={execNewDraft.name}
            onChange={e => setExecNewDraft(d => ({ ...d, name: e.target.value }))}
          />

          <label style={{ ...styles.label, marginTop: '16px' }}>Executive Calendars to Read</label>
          <p style={styles.hint}>Whose calendar to fetch each week. Service account must be authorized via domain-wide delegation.</p>
          {renderChipList(
            execNewDraft.executiveEmails,
            (email) => setExecNewDraft(d => ({ ...d, executiveEmails: d.executiveEmails.filter(e => e !== email) }))
          )}
          {renderAddInput(
            execNewDraftExecInput,
            setExecNewDraftExecInput,
            () => {
              const v = execNewDraftExecInput.trim().toLowerCase();
              if (!v) return;
              if (execNewDraft.executiveEmails.includes(v)) return;
              setExecNewDraft(d => ({ ...d, executiveEmails: [...d.executiveEmails, v] }));
              setExecNewDraftExecInput('');
            },
            'Add executive email…'
          )}

          <label style={{ ...styles.label, marginTop: '16px' }}>Email Recipients</label>
          <p style={styles.hint}>Who receives the weekly schedule email.</p>
          {renderChipList(
            execNewDraft.recipientEmails,
            (email) => setExecNewDraft(d => ({ ...d, recipientEmails: d.recipientEmails.filter(e => e !== email) }))
          )}
          {renderAddInput(
            execNewDraftRecipInput,
            setExecNewDraftRecipInput,
            () => {
              const v = execNewDraftRecipInput.trim().toLowerCase();
              if (!v) return;
              if (execNewDraft.recipientEmails.includes(v)) return;
              setExecNewDraft(d => ({ ...d, recipientEmails: [...d.recipientEmails, v] }));
              setExecNewDraftRecipInput('');
            },
            'Add recipient email…'
          )}

          <div style={{ ...styles.fieldsGrid, marginTop: '16px' }}>
            <div style={styles.fieldGroup}>
              <label style={styles.label}>Schedule (cron)</label>
              <input
                style={styles.input}
                type="text"
                value={execNewDraft.scheduleCron}
                onChange={e => setExecNewDraft(d => ({ ...d, scheduleCron: e.target.value }))}
              />
              <span style={styles.hint}>Default: <code>0 6 * * 0</code> (Sunday 6 AM)</span>
            </div>
            <div style={styles.fieldGroup}>
              <label style={styles.label}>Timezone (IANA)</label>
              <input
                style={styles.input}
                type="text"
                value={execNewDraft.scheduleTimezone}
                onChange={e => setExecNewDraft(d => ({ ...d, scheduleTimezone: e.target.value }))}
              />
              <span style={styles.hint}>Default: <code>America/Los_Angeles</code></span>
            </div>
            <div style={styles.fieldGroup}>
              <label style={styles.label}>Days Ahead</label>
              <input
                style={{ ...styles.input, maxWidth: '120px' }}
                type="number"
                min={1}
                max={14}
                value={execNewDraft.daysAhead}
                onChange={e => setExecNewDraft(d => ({ ...d, daysAhead: parseInt(e.target.value) || 7 }))}
              />
              <span style={styles.hint}>1–14 days from run time</span>
            </div>
          </div>

          <div style={styles.saveRow}>
            <button
              onClick={handleExecCreate}
              disabled={execCreating}
              style={{ ...styles.saveButton, ...(execCreating ? styles.buttonDisabled : {}) }}
            >
              {execCreating ? 'Creating…' : 'Create Task'}
            </button>
          </div>
        </div>
      )}

      {execTasks.map(task => {
        const card = execCardState[task.id];
        if (!card) return null;
        const edit = card.edit || task;
        return (
          <React.Fragment key={task.id}>
            {renderTaskCard(
              `Calendar — ${task.name}`,
              formatCronSchedule(task.scheduleCron, task.scheduleTimezone),
              `Reads the upcoming-${task.daysAhead}-day calendar for ${task.executiveEmails.join(', ')} via the service account, renders a day-by-day schedule, and emails it to ${task.recipientEmails.join(', ')}.`,
              {
                settings: edit,
                runs: card.runs,
                loading: false,
                error: card.error,
                saving: card.saving,
                triggering: card.triggering,
                message: card.message,
                expanded: card.expanded,
                expandedRunId: card.expandedRunId,
              },
              () => updateExecCard(task.id, { expanded: !card.expanded }),
              () => handleExecPauseResume(task.id),
              () => handleExecTrigger(task.id),
              card.dryRun ? 'Run Dry' : 'Run Now',
              task.enabled,
              <>
                <label style={styles.label}>Task Name</label>
                <input
                  style={{ ...styles.input, width: '100%', maxWidth: '500px' }}
                  type="text"
                  value={edit.name || ''}
                  onChange={e => updateExecEdit(task.id, { name: e.target.value })}
                />

                <label style={{ ...styles.label, marginTop: '16px' }}>Executive Calendars</label>
                <p style={styles.hint}>Whose calendar to read for the upcoming-week summary.</p>
                {renderChipList(
                  edit.executiveEmails || [],
                  (email) => removeExecChip(task.id, 'executiveEmails', email)
                )}
                {renderAddInput(
                  execNewExecInput[task.id] || '',
                  (v) => setExecNewExecInput(prev => ({ ...prev, [task.id]: v })),
                  () => addExecChip(
                    task.id,
                    'executiveEmails',
                    execNewExecInput[task.id] || '',
                    setExecNewExecInput,
                    execNewExecInput
                  ),
                  'Add executive email…'
                )}

                <label style={{ ...styles.label, marginTop: '16px' }}>Email Recipients</label>
                <p style={styles.hint}>Who receives the weekly schedule email.</p>
                {renderChipList(
                  edit.recipientEmails || [],
                  (email) => removeExecChip(task.id, 'recipientEmails', email)
                )}
                {renderAddInput(
                  execNewRecipInput[task.id] || '',
                  (v) => setExecNewRecipInput(prev => ({ ...prev, [task.id]: v })),
                  () => addExecChip(
                    task.id,
                    'recipientEmails',
                    execNewRecipInput[task.id] || '',
                    setExecNewRecipInput,
                    execNewRecipInput
                  ),
                  'Add recipient email…'
                )}

                <div style={{ ...styles.fieldsGrid, marginTop: '16px' }}>
                  <div style={styles.fieldGroup}>
                    <label style={styles.label}>Schedule (cron)</label>
                    <input
                      style={styles.input}
                      type="text"
                      value={edit.scheduleCron || ''}
                      onChange={e => updateExecEdit(task.id, { scheduleCron: e.target.value })}
                    />
                    <span style={styles.hint}>Sun 6 AM = <code>0 6 * * 0</code></span>
                  </div>
                  <div style={styles.fieldGroup}>
                    <label style={styles.label}>Timezone (IANA)</label>
                    <input
                      style={styles.input}
                      type="text"
                      value={edit.scheduleTimezone || ''}
                      onChange={e => updateExecEdit(task.id, { scheduleTimezone: e.target.value })}
                    />
                  </div>
                  <div style={styles.fieldGroup}>
                    <label style={styles.label}>Days Ahead</label>
                    <input
                      style={{ ...styles.input, maxWidth: '120px' }}
                      type="number"
                      min={1}
                      max={14}
                      value={edit.daysAhead ?? 7}
                      onChange={e => updateExecEdit(task.id, { daysAhead: parseInt(e.target.value) || 7 })}
                    />
                  </div>
                </div>

                <div style={{ ...styles.saveRow, justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap' as const, gap: '12px' }}>
                  <label style={styles.checkboxLabel}>
                    <input
                      type="checkbox"
                      checked={card.dryRun}
                      onChange={e => updateExecCard(task.id, { dryRun: e.target.checked })}
                    />
                    <span>Dry run on next "Run Now" (no email sent)</span>
                  </label>
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <button
                      onClick={() => handleExecDelete(task.id)}
                      style={{
                        ...styles.headerButton,
                        color: 'var(--danger)',
                        borderColor: 'var(--danger)',
                      }}
                    >
                      Delete Task
                    </button>
                    <button
                      onClick={() => handleExecSave(task.id)}
                      disabled={card.saving}
                      style={{ ...styles.saveButton, ...(card.saving ? styles.buttonDisabled : {}) }}
                    >
                      {card.saving ? 'Saving…' : 'Save Settings'}
                    </button>
                  </div>
                </div>
              </>,
              renderRunHistory(
                card.runs,
                card.expandedRunId,
                (id) => updateExecCard(task.id, { expandedRunId: id }),
                (run) => (
                  <>
                    {run.stats && (
                      <>
                        <strong>Total Events:</strong> {run.stats.totalEvents ?? '-'}<br />
                        {run.stats.perExecutiveEventCounts && Object.keys(run.stats.perExecutiveEventCounts).length > 0 && (
                          <>
                            <strong>Per Executive:</strong>{' '}
                            {Object.entries(run.stats.perExecutiveEventCounts).map(([email, count]) =>
                              `${email}: ${count}`
                            ).join(' · ')}
                            <br />
                          </>
                        )}
                        <strong>Email Sent To:</strong>{' '}
                        {run.stats.emailSentTo?.length > 0
                          ? run.stats.emailSentTo.join(', ')
                          : (run.stats.dryRun ? '(dry run — not sent)' : 'None')}<br />
                        {run.stats.dryRun && <><strong>Mode:</strong> DRY RUN<br /></>}
                      </>
                    )}
                  </>
                ),
                () => refreshExecRuns(task.id)
              )
            )}
          </React.Fragment>
        );
      })}

      {/* ==================== Direct Sold Processing Reminder ==================== */}
      {renderTaskCard(
        'Direct Sold Processing Reminder',
        '3rd of month, 6 AM ET',
        'Sends a monthly reminder email to upload the GAM Direct Sold report and start processing from the Financials dashboard. Processing no longer runs automatically — it starts only after the month’s GAM CSV is uploaded (the reminder is skipped when the report is already in).',
        directSoldState,
        () => setDirectSoldState(prev => ({ ...prev, expanded: !prev.expanded })),
        handleDirectSoldPauseResume,
        handleDirectSoldTrigger,
        'Send Test Reminder',
        directSoldState.settings?.schedule?.enabled ?? true,
        // Config content
        <>
          {/* Reminder Recipients */}
          <label style={styles.label}>Reminder Recipients</label>
          <p style={styles.hint}>Who receives the monthly upload reminder (also notified when a manual run completes or fails).</p>
          {renderChipList(
            directSoldState.settings?.notifications?.notificationEmails || [],
            removeDirectSoldRecipient
          )}
          {renderAddInput(
            directSoldNewRecipient,
            setDirectSoldNewRecipient,
            () => addDirectSoldRecipient(directSoldNewRecipient),
            'Add notification email...'
          )}

          {/* Notification toggles */}
          <div style={{ ...styles.fieldsGrid, marginTop: '16px' }}>
            <div style={styles.fieldGroup}>
              <label style={styles.checkboxLabel}>
                <input
                  type="checkbox"
                  checked={directSoldState.settings?.notifications?.emailOnComplete ?? true}
                  onChange={e => setDirectSoldState(prev => ({
                    ...prev,
                    settings: {
                      ...prev.settings,
                      notifications: {
                        ...prev.settings.notifications,
                        emailOnComplete: e.target.checked,
                      },
                    },
                  }))}
                />
                <span>Email on Complete</span>
              </label>
            </div>
            <div style={styles.fieldGroup}>
              <label style={styles.checkboxLabel}>
                <input
                  type="checkbox"
                  checked={directSoldState.settings?.notifications?.emailOnFailure ?? true}
                  onChange={e => setDirectSoldState(prev => ({
                    ...prev,
                    settings: {
                      ...prev.settings,
                      notifications: {
                        ...prev.settings.notifications,
                        emailOnFailure: e.target.checked,
                      },
                    },
                  }))}
                />
                <span>Email on Failure</span>
              </label>
            </div>
          </div>

          {/* Save */}
          <div style={styles.saveRow}>
            <button
              onClick={handleDirectSoldSave}
              disabled={directSoldState.saving}
              style={{ ...styles.saveButton, ...(directSoldState.saving ? styles.buttonDisabled : {}) }}
            >
              {directSoldState.saving ? 'Saving...' : 'Save Settings'}
            </button>
          </div>
        </>,
        // Run history content
        renderRunHistory(
          directSoldState.runs,
          directSoldState.expandedRunId,
          (id) => setDirectSoldState(prev => ({ ...prev, expandedRunId: id })),
          (run) => (
            <>
              {run.monthDisplay && <><strong>Month:</strong> {run.monthDisplay}<br /></>}
              {run.stats && (
                <>
                  <strong>Rows Processed:</strong> {run.stats.processedRows ?? '-'}<br />
                  <strong>Channels:</strong> {run.stats.channelCount ?? '-'}<br />
                  <strong>Total Earnings:</strong> {run.stats.totalChannelEarnings != null ? `$${run.stats.totalChannelEarnings.toFixed(2)}` : '-'}<br />
                </>
              )}
            </>
          ),
          refreshDirectSoldRuns
        )
      )}

      {/* ==================== RFP Inbox Scanner ==================== */}
      {renderTaskCard(
        'RFP Inbox Scanner',
        'Every 15 min',
        'Monitors the sales inbox for incoming RFP emails, extracts key details (advertiser, budget, due date), creates organized Drive folders with spec documents, and notifies the sales team.',
        rfpState,
        () => setRfpState(prev => ({ ...prev, expanded: !prev.expanded })),
        handleRfpPauseResume,
        handleRfpTrigger,
        'Scan Now',
        rfpState.settings?.enabled ?? true,
        // Config content
        <>
          {/* Notification Recipients */}
          <label style={styles.label}>Notification Recipients</label>
          <p style={styles.hint}>Who gets notified when a new RFP is detected.</p>
          {renderChipList(
            rfpState.settings?.notificationRecipients || [],
            (email) => removeChip('notificationRecipients', email, setRfpState)
          )}
          {renderAddInput(
            rfpNewRecipient,
            setRfpNewRecipient,
            () => addChip('notificationRecipients', rfpNewRecipient, setRfpState, setRfpNewRecipient),
            'Add recipient email...'
          )}

          {/* Exclude Patterns */}
          <label style={{ ...styles.label, marginTop: '16px' }}>Exclude Patterns</label>
          <p style={styles.hint}>Emails with subjects matching these patterns are skipped.</p>
          {renderChipList(
            rfpState.settings?.excludePatterns || [],
            (pattern) => removeChip('excludePatterns', pattern, setRfpState)
          )}
          {renderAddInput(
            rfpNewExclude,
            setRfpNewExclude,
            () => addChip('excludePatterns', rfpNewExclude, setRfpState, setRfpNewExclude),
            'Add exclude pattern...',
            'text'
          )}

          {/* Config fields */}
          <div style={{ ...styles.fieldsGrid, marginTop: '16px' }}>
            <div style={styles.fieldGroup}>
              <label style={styles.label}>Search Window (hours)</label>
              <input
                style={{ ...styles.input, maxWidth: '120px' }}
                type="number"
                min={1}
                max={24}
                value={rfpState.settings?.searchTimeWindowHours ?? 4}
                onChange={e => setRfpState(prev => ({
                  ...prev,
                  settings: { ...prev.settings, searchTimeWindowHours: parseInt(e.target.value) || 4 },
                }))}
              />
            </div>
            <div style={styles.fieldGroup}>
              <label style={styles.label}>Max RFPs Per Run</label>
              <input
                style={{ ...styles.input, maxWidth: '120px' }}
                type="number"
                min={1}
                max={50}
                value={rfpState.settings?.maxRfpsPerRun ?? 10}
                onChange={e => setRfpState(prev => ({
                  ...prev,
                  settings: { ...prev.settings, maxRfpsPerRun: parseInt(e.target.value) || 10 },
                }))}
              />
            </div>
          </div>

          {/* Read-only info */}
          <div style={{ ...styles.fieldsGrid, marginTop: '16px' }}>
            <div style={styles.fieldGroup}>
              <label style={styles.label}>Sales Inbox</label>
              <span style={{ fontSize: '13px', color: 'var(--fg-2)' }}>
                {rfpState.settings?.salesInbox || 'sales@culturegenesis.com'}
              </span>
            </div>
            <div style={styles.fieldGroup}>
              <label style={styles.label}>Polling Interval</label>
              <span style={{ fontSize: '13px', color: 'var(--fg-2)' }}>Every 15 minutes</span>
            </div>
          </div>

          {/* Save */}
          <div style={styles.saveRow}>
            <button
              onClick={handleRfpSave}
              disabled={rfpState.saving}
              style={{ ...styles.saveButton, ...(rfpState.saving ? styles.buttonDisabled : {}) }}
            >
              {rfpState.saving ? 'Saving...' : 'Save Settings'}
            </button>
          </div>
        </>,
        // Run history content
        renderRunHistory(
          rfpState.runs,
          rfpState.expandedRunId,
          (id) => setRfpState(prev => ({ ...prev, expandedRunId: id })),
          (run) => (
            <>
              {run.stats && (
                <>
                  <strong>Emails Found:</strong> {run.stats.emailsFound ?? '-'}<br />
                  <strong>Processed:</strong> {run.stats.processed ?? '-'} | <strong>Skipped:</strong> {run.stats.skipped ?? '-'} | <strong>Errors:</strong> {run.stats.errors ?? '-'}<br />
                  <strong>New RFPs:</strong> {run.stats.newRfps ?? '-'} | <strong>Updates:</strong> {run.stats.updates ?? '-'} | <strong>Noise:</strong> {run.stats.noise ?? '-'}<br />
                </>
              )}
            </>
          ),
          refreshRfpRuns
        )
      )}

      {/* ==================== IO Approval Forwarder ==================== */}
      {renderTaskCard(
        'IO Approval Forwarder',
        'Every 15 min',
        'Watches the sales inbox for incoming IO approvals from agencies (Mediaocean and others), then forwards each match — with the original PDF attached — to the IO approval team under a clean "IO Approval - Needs Attention" subject line.',
        ioApprovalState,
        () => setIoApprovalState(prev => ({ ...prev, expanded: !prev.expanded })),
        handleIoApprovalPauseResume,
        handleIoApprovalTrigger,
        'Scan Now',
        ioApprovalState.settings?.enabled ?? true,
        // Config content
        <>
          {/* Notification Recipients */}
          <label style={styles.label}>Forward To</label>
          <p style={styles.hint}>Recipients of every forwarded IO approval.</p>
          {renderChipList(
            ioApprovalState.settings?.notificationRecipients || [],
            (email) => removeChip('notificationRecipients', email, setIoApprovalState)
          )}
          {renderAddInput(
            ioApprovalNewRecipient,
            setIoApprovalNewRecipient,
            () => addChip('notificationRecipients', ioApprovalNewRecipient, setIoApprovalState, setIoApprovalNewRecipient),
            'Add recipient email...'
          )}

          {/* Subject Patterns */}
          <label style={{ ...styles.label, marginTop: '16px' }}>Subject Match Patterns</label>
          <p style={styles.hint}>An email is treated as an IO approval if its subject contains any of these phrases (case-insensitive).</p>
          {renderChipList(
            ioApprovalState.settings?.subjectPatterns || [],
            (pattern) => removeChip('subjectPatterns', pattern, setIoApprovalState)
          )}
          {renderAddInput(
            ioApprovalNewSubjectPattern,
            setIoApprovalNewSubjectPattern,
            () => addChip('subjectPatterns', ioApprovalNewSubjectPattern, setIoApprovalState, setIoApprovalNewSubjectPattern),
            'Add subject pattern...',
            'text'
          )}

          {/* Exclude Patterns */}
          <label style={{ ...styles.label, marginTop: '16px' }}>Exclude Patterns</label>
          <p style={styles.hint}>Emails with subjects matching these patterns are skipped (auto-replies, bounces, etc.).</p>
          {renderChipList(
            ioApprovalState.settings?.excludePatterns || [],
            (pattern) => removeChip('excludePatterns', pattern, setIoApprovalState)
          )}
          {renderAddInput(
            ioApprovalNewExclude,
            setIoApprovalNewExclude,
            () => addChip('excludePatterns', ioApprovalNewExclude, setIoApprovalState, setIoApprovalNewExclude),
            'Add exclude pattern...',
            'text'
          )}

          {/* Subject + numeric fields */}
          <div style={{ ...styles.fieldsGrid, marginTop: '16px' }}>
            <div style={styles.fieldGroup}>
              <label style={styles.label}>Forwarded Subject</label>
              <input
                style={{ ...styles.input, maxWidth: '320px' }}
                type="text"
                value={ioApprovalState.settings?.forwardedSubject ?? 'IO Approval - Needs Attention'}
                onChange={e => setIoApprovalState(prev => ({
                  ...prev,
                  settings: { ...prev.settings, forwardedSubject: e.target.value },
                }))}
              />
            </div>
            <div style={styles.fieldGroup}>
              <label style={styles.label}>Lookback (hours)</label>
              <input
                style={{ ...styles.input, maxWidth: '120px' }}
                type="number"
                min={1}
                max={48}
                value={ioApprovalState.settings?.lookbackHours ?? 24}
                onChange={e => setIoApprovalState(prev => ({
                  ...prev,
                  settings: { ...prev.settings, lookbackHours: parseInt(e.target.value) || 24 },
                }))}
              />
            </div>
            <div style={styles.fieldGroup}>
              <label style={styles.label}>Max Emails Per Run</label>
              <input
                style={{ ...styles.input, maxWidth: '120px' }}
                type="number"
                min={1}
                max={100}
                value={ioApprovalState.settings?.maxEmailsPerRun ?? 25}
                onChange={e => setIoApprovalState(prev => ({
                  ...prev,
                  settings: { ...prev.settings, maxEmailsPerRun: parseInt(e.target.value) || 25 },
                }))}
              />
            </div>
          </div>

          {/* Read-only info */}
          <div style={{ ...styles.fieldsGrid, marginTop: '16px' }}>
            <div style={styles.fieldGroup}>
              <label style={styles.label}>Source Inbox</label>
              <span style={{ fontSize: '13px', color: 'var(--fg-2)' }}>
                {ioApprovalState.settings?.salesInbox || 'sales@culturegenesis.com'}
              </span>
            </div>
            <div style={styles.fieldGroup}>
              <label style={styles.label}>Polling Interval</label>
              <span style={{ fontSize: '13px', color: 'var(--fg-2)' }}>Every 15 minutes</span>
            </div>
          </div>

          {/* Save */}
          <div style={styles.saveRow}>
            <button
              onClick={handleIoApprovalSave}
              disabled={ioApprovalState.saving}
              style={{ ...styles.saveButton, ...(ioApprovalState.saving ? styles.buttonDisabled : {}) }}
            >
              {ioApprovalState.saving ? 'Saving...' : 'Save Settings'}
            </button>
          </div>
        </>,
        // Run history content
        renderRunHistory(
          ioApprovalState.runs,
          ioApprovalState.expandedRunId,
          (id) => setIoApprovalState(prev => ({ ...prev, expandedRunId: id })),
          (run) => (
            <>
              {run.stats && (
                <>
                  <strong>Emails Found:</strong> {run.stats.emailsFound ?? '-'}<br />
                  <strong>Forwarded:</strong> {run.stats.processed ?? '-'} | <strong>Skipped:</strong> {run.stats.skipped ?? '-'} | <strong>Errors:</strong> {run.stats.errors ?? '-'}<br />
                  <strong>Attachments Omitted:</strong> {run.stats.attachmentsSkipped ?? 0}<br />
                </>
              )}
            </>
          ),
          refreshIoApprovalRuns
        )
      )}
    </div>
  );
};

// ============================================================
// Styles
// ============================================================

const styles: Record<string, React.CSSProperties> = {
  card: {
    background: 'var(--bg-surface-1)',
    borderRadius: '12px',
    border: '1px solid var(--border-1)',
    marginBottom: '20px',
    overflow: 'hidden',
  },
  cardHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '16px 24px',
    cursor: 'pointer',
    borderBottom: '1px solid var(--border-1)',
    userSelect: 'none' as const,
  },
  cardHeaderLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    flexWrap: 'wrap' as const,
  },
  cardHeaderRight: {
    display: 'flex',
    gap: '8px',
    flexShrink: 0,
  },
  cardTitle: {
    margin: 0,
    fontSize: '15px',
    fontWeight: 600,
    color: 'var(--fg-1)',
  },
  scheduleText: {
    fontSize: '12px',
    color: 'var(--fg-3)',
    marginLeft: '4px',
  },
  headerButton: {
    padding: '6px 14px',
    background: 'var(--accent-soft)',
    color: 'var(--fg-1)',
    border: '1px solid var(--border-1)',
    borderRadius: '6px',
    fontSize: '12px',
    fontWeight: 500,
    cursor: 'pointer',
    fontFamily: 'var(--font-sans)',
    boxShadow: 'none',
  },
  headerButtonPrimary: {
    padding: '6px 14px',
    background: 'var(--accent)',
    color: '#fff',
    border: 'none',
    borderRadius: '6px',
    fontSize: '12px',
    fontWeight: 600,
    cursor: 'pointer',
    fontFamily: 'var(--font-sans)',
    boxShadow: 'none',
  },
  buttonDisabled: {
    opacity: 0.6,
    cursor: 'not-allowed',
  },
  cardDescription: {
    padding: '12px 24px 0',
    fontSize: '13px',
    lineHeight: 1.5,
    color: 'var(--fg-2)',
  },
  cardSection: {
    padding: '16px 24px',
    borderBottom: '1px solid var(--border-1)',
  },
  sectionTitle: {
    margin: '0 0 12px 0',
    fontSize: '14px',
    fontWeight: 600,
    color: 'var(--fg-1)',
  },
  label: {
    display: 'block',
    fontSize: '13px',
    fontWeight: 500,
    color: 'var(--fg-2)',
    marginBottom: '2px',
  },
  hint: {
    fontSize: '11px',
    color: 'var(--fg-3)',
    marginTop: '2px',
    marginBottom: '8px',
  },
  input: {
    padding: '7px 10px',
    border: '1px solid var(--border-1)',
    borderRadius: '6px',
    fontSize: '13px',
    fontFamily: 'var(--font-sans)',
    color: 'var(--fg-1)',
    background: 'var(--bg-app)',
    outline: 'none',
  },
  chipList: {
    display: 'flex',
    flexWrap: 'wrap' as const,
    gap: '6px',
    marginBottom: '10px',
    minHeight: '28px',
  },
  chip: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '4px',
    padding: '3px 10px',
    background: 'var(--accent-soft)',
    borderRadius: '14px',
    fontSize: '12px',
    color: 'var(--fg-1)',
    border: '1px solid var(--border-1)',
  },
  chipRemove: {
    background: 'none',
    border: 'none',
    color: 'var(--fg-3)',
    fontSize: '15px',
    cursor: 'pointer',
    padding: '0 2px',
    lineHeight: 1,
    fontFamily: 'var(--font-sans)',
    boxShadow: 'none',
  },
  addRow: {
    display: 'flex',
    gap: '6px',
    alignItems: 'center',
    maxWidth: '380px',
  },
  addButton: {
    padding: '7px 14px',
    background: 'var(--accent-soft)',
    color: 'var(--fg-1)',
    border: '1px solid var(--border-1)',
    borderRadius: '6px',
    fontSize: '12px',
    fontWeight: 500,
    cursor: 'pointer',
    fontFamily: 'var(--font-sans)',
    whiteSpace: 'nowrap' as const,
    boxShadow: 'none',
  },
  fieldsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
    gap: '16px',
  },
  fieldGroup: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '4px',
  },
  checkboxLabel: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    fontSize: '13px',
    color: 'var(--fg-1)',
    cursor: 'pointer',
  },
  saveRow: {
    display: 'flex',
    justifyContent: 'flex-end',
    marginTop: '16px',
  },
  saveButton: {
    padding: '8px 20px',
    background: 'var(--accent)',
    color: '#fff',
    border: 'none',
    borderRadius: '6px',
    fontSize: '13px',
    fontWeight: 600,
    cursor: 'pointer',
    fontFamily: 'var(--font-sans)',
    boxShadow: 'none',
  },
  table: tableStyles.table,
  th: tableStyles.th,
  td: tableStyles.td,
  refreshButton: {
    padding: '4px 12px',
    background: 'none',
    color: 'var(--fg-2)',
    border: '1px solid var(--border-1)',
    borderRadius: '4px',
    fontSize: '11px',
    fontWeight: 500,
    cursor: 'pointer',
    fontFamily: 'var(--font-sans)',
    boxShadow: 'none',
  },
  errorText: {
    color: 'var(--danger)',
    fontSize: '13px',
    padding: '8px 24px 0',
    margin: 0,
  },
  successText: {
    color: 'var(--success)',
    fontSize: '13px',
    padding: '8px 24px 0',
    margin: 0,
  },
};

export default ScheduledEmailsTab;
